import { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faArrowRotateLeft } from '@fortawesome/pro-regular-svg-icons';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ContentCard from '@v2/shared/components/ContentCard';
import FaqAccordion from '@v2/shared/components/FaqAccordion';
import PageHeading from '@v2/shared/components/PageHeading';
import { cn } from '@v2/shared/lib/utils';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { liveAgentHours } from '@v2/shared/lib/supportHours';

export type Props = {
  /**
   * Where "manage DiscountLock" goes, from DiscountLockUrlGenerator — the account page itself
   * on every site today, and the old `customer.php?xCmd=dlshow` portal on any site whose
   * `new_discountlock_experience` option is turned off. Built by the server because that
   * decision is the server's, and absolute for the same reason it always was.
   */
  accountUrl: string;
};

/** The comparison's cells, which read left — including the header row. */
const CELL = 'border border-solid border-slate-200 px-3.5 py-2.5 text-left align-top';
const HEADER_CELL = `${CELL} bg-slate-50 font-bold text-slate-900`;

/** Our own column, which the comparison exists to draw the eye to. */
const US_CELL = `${CELL} bg-primary-50`;
const US_HEADER_CELL = `${CELL} bg-primary-50 font-bold text-primary`;

/** Room under the fixed site header for whichever section an anchor lands on. */
const ANCHOR = 'scroll-mt-24';

/** A section heading's supporting line, which prose has no element for. */
const TAGLINE = 'mb-4 mt-1 text-sm text-slate-500';

/** The two shapes the page's calls to action come in. */
const BUTTON = 'rounded-lg px-5 py-2.5 text-sm font-bold no-underline';
const BUTTON_FILLED = `${BUTTON} bg-primary text-white hover:bg-primary-700`;
const BUTTON_OUTLINE = `${BUTTON} border border-solid border-primary bg-white text-primary hover:bg-primary-50`;

/**
 * The summary strip that overlaps the hero — the whole page in four lines, each one a
 * link to the section that expands on it.
 */
const GLANCE: { href: string; term: string; detail: ReactNode }[] = [
  {
    href: '#savings',
    term: 'Guaranteed savings',
    detail: (
      <>
        At least <b className="font-bold text-primary">10% off</b> the list price at every renewal
      </>
    ),
  },
  {
    href: '#how',
    term: 'No missed issues',
    detail: (
      <>
        Renews on your <b className="font-bold text-primary">order anniversary</b>; new time is
        added to the end of your term
      </>
    ),
  },
  {
    href: '#reminders',
    term: 'Two reminders',
    detail: (
      <>
        Emails <b className="font-bold text-primary">30 and 7 days</b> ahead, the second one with
        the exact price
      </>
    ),
  },
  {
    href: '#cancel',
    term: 'Cancel anytime',
    detail: (
      <>
        In <b className="font-bold text-primary">your account</b> in about a minute, or by
        contacting us
      </>
    ),
  },
];

const JUMP_LINKS: { href: string; label: string }[] = [
  { href: '#how', label: 'How it works' },
  { href: '#savings', label: 'Guaranteed savings' },
  { href: '#reminders', label: 'Reminders & timing' },
  { href: '#cancel', label: 'Cancel anytime' },
  { href: '#compare', label: 'Compare' },
  { href: '#faqs', label: 'FAQs' },
];

const STEPS: { lead: string; body: string }[] = [
  {
    lead: 'It’s included with your order.',
    body:
      'DiscountLock comes with every eligible subscription you buy, where permitted by law. ' +
      'There’s no fee, and it locks in your discount for future renewals from day one.',
  },
  {
    lead: 'We email you before every renewal.',
    body:
      'A heads-up 30 days ahead, then the exact price 7 days ahead. Nothing is charged without ' +
      'notice.',
  },
  {
    lead: 'We renew on your order anniversary.',
    body:
      'The renewal processes near the end of the term you bought from us, counted from your order ' +
      'date. The new term is added to the end of your current one, so nothing is lost if it ' +
      'renews early, and today’s discounted price is locked in.',
  },
];

const FACTS: { term: string; value: string; detail: string }[] = [
  { term: 'Minimum discount', value: '10% off list', detail: 'at every renewal, guaranteed' },
  {
    term: 'If we can’t hit 10%',
    value: 'We don’t renew',
    detail: 'we email you and let you decide',
  },
  { term: 'Renewal fee', value: '$0', detail: 'DiscountLock is free to use' },
];

const TIMELINE: { dot: string; term: string; detail: string }[] = [
  {
    dot: '−30',
    term: '30 days before',
    detail: 'Heads-up email with the renewal date and a link to manage or turn off DiscountLock.',
  },
  {
    dot: '−7',
    term: '7 days before',
    detail:
      'Price email with the exact amount you’ll be charged, including any tax. Still time to ' +
      'cancel.',
  },
  {
    dot: '0',
    term: 'Process date',
    detail: 'Your renewal is charged to the payment method on file and sent to the publisher.',
  },
];

/** When the renewal runs, by what was bought — counted from the order, never the label. */
const PROCESSING: { type: string; when: string }[] = [
  {
    type: 'Print, 1 year or longer',
    when: '83 days before the end of the term you bought from us',
  },
  {
    type: 'Print, less than 1 year',
    when: '38 days before the end of the term you bought from us',
  },
  { type: 'Digital', when: '13 days before the end of the term you bought from us' },
];

/**
 * /discountlock.
 *
 * A benefits page rather than a policy page, so it is the one institutional page that
 * leads with a hero of its own instead of the shared `PageHeading` band: it carries two
 * calls to action and the summary strip overlaps it, neither of which that band does or
 * should learn. /reviews is built the same way and for the same reason.
 *
 * The copy answers the questions support actually gets, in the order it gets them — above
 * all why a renewal can process months before the date printed on a magazine label, which
 * is the single largest driver of DiscountLock contacts. It renews on the anniversary of
 * the order placed here, not on the subscription's end date, and the new term is added to
 * the end of the current one.
 *
 * Every dated terms document incorporates the benefits "described on that page" by
 * reference, so what is said here is said in the terms. Not a reason not to change the
 * page — it was changed deliberately — but a reason to change it knowingly.
 */
export default function DiscountLockBenefits({ accountUrl }: Props) {
  const { buildUrl } = useUrlGenerator();

  const refundUrl = buildUrl('/refund-policy');
  const contactUrl = buildUrl('/contact');
  const ordersUrl = buildUrl('/customer?xCmd=orders');

  const faqs: { question: string; answer: ReactNode }[] = [
    {
      question: 'Does turning off DiscountLock cancel my subscription?',
      answer: (
        <>
          No. Your current subscription continues through the end of its current term. Only future
          renewals stop. If you want to cancel the current order and get a refund of unsent issues,
          do that from <a href={ordersUrl}>My Orders</a> instead.
        </>
      ),
    },
    {
      question: 'Why was I charged months before my subscription expires?',
      answer: (
        <>
          Because DiscountLock renews on your order anniversary, not the date on your label. If you
          had a subscription running before you ordered from us, the two won’t line up. Nothing is
          lost: the renewal is added to the end of your current term at today’s discounted price,
          with no duplicate issues. If you’d rather wait, delay the renewal or set a date from your{' '}
          <a href={accountUrl}>DiscountLock page</a>. We email you 30 and 7 days before any charge
          either way.
        </>
      ),
    },
    {
      question: 'Will I know the price before I’m charged?',
      answer: (
        <>
          Yes. The email we send 7 days before your renewal shows the exact amount, including any
          tax. It will always be at least 10% off the list price at that time. If we can’t offer
          that discount, we won’t renew automatically.
        </>
      ),
    },
    {
      question: 'Is DiscountLock available everywhere?',
      answer: (
        <>
          In some states, including Vermont, DiscountLock is enrolled only if you opt in at
          checkout. If it isn’t available to you, it won’t be applied to your order and your
          subscription will simply end at the close of its term. You can always place a new order to
          continue.
        </>
      ),
    },
    {
      question: 'Can I add DiscountLock to a subscription I already have?',
      answer: (
        <>
          DiscountLock is set up when an eligible subscription is ordered, so it can’t be added to
          an existing order afterward. It also can’t be turned back on once you’ve disabled it. To
          renew a subscription without DiscountLock, place a new order when your term is ending.
        </>
      ),
    },
    {
      question: 'What if my card has expired or the payment fails?',
      answer: (
        <>
          We’ll email you and give you time to update your payment method in your account. If the
          payment can’t be completed, the renewal doesn’t process and your subscription ends at the
          close of its current term. Nothing is charged twice.
        </>
      ),
    },
    {
      question: 'I got a renewal notice in the mail. Is that from you?',
      answer: (
        <>
          No. We only send renewal notices by email. Publishers often mail their own renewal
          invoices when a subscription is nearing its end. You can ignore those; your DiscountLock
          renewal is handled here.
        </>
      ),
    },
    {
      question: 'I cancelled on the publisher’s website. Does that stop DiscountLock?',
      answer: (
        <>
          No. DiscountLock is managed in your DiscountMags account, not with the publisher. Turn it
          off <a href={accountUrl}>here</a> or contact us and we’ll do it for you.
        </>
      ),
    },
    {
      question: 'Where do I see which subscriptions have DiscountLock?',
      answer: (
        <>
          Sign in and open <a href={accountUrl}>DiscountLock</a> in your account. It lists every
          subscription enrolled, its next renewal date, and a Disable button for each.
        </>
      ),
    },
  ];

  return (
    <>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'DiscountLock' }]} />

      {/* The hero, and the room under it the summary strip overlaps into. */}
      <PageHeading
        hero
        eyebrow="DiscountLock"
        intro="DiscountLock is our free auto-renewal service. It renews your subscription on your order anniversary so you never miss an issue, always at a discount, and it emails you twice before any charge. Turn it off anytime from your account, or just ask us and we’ll do it for you."
        actions={
          <>
            <a href={accountUrl} className={`${BUTTON} bg-white text-primary hover:bg-primary-50`}>
              Manage DiscountLock
            </a>
            <a
              href="#cancel"
              className={`${BUTTON} border border-solid border-white/70 text-white hover:bg-white/10`}
            >
              How to turn it off
            </a>
          </>
        }
        footnote={
          <>
            No fees. No commitment. Renewals are covered by our{' '}
            <a href={refundUrl} className="text-white underline underline-offset-2">
              refund policy
            </a>{' '}
            like any other order. Void where prohibited.
          </>
        }
      >
        Your subscriptions renew themselves. You stay in control.
      </PageHeading>

      <ul className="relative z-10 mx-3 -mt-12 grid list-none grid-cols-1 rounded-lg border border-solid border-slate-200 bg-white p-0 shadow-sm sm:grid-cols-2 md:mx-6 lg:grid-cols-4">
        {GLANCE.map((cell, index) => (
          <li
            key={cell.href}
            className={cn(
              'border-0 border-solid border-slate-200',
              // Dividers between cells: one column on a phone, two from `sm`, four from
              // `lg`. Each breakpoint states both edges, because a rule that only adds
              // borders cannot take back the row above's.
              index > 0 && 'border-t',
              index % 2 === 1 && 'sm:border-l',
              index < 2 && 'sm:border-t-0',
              index > 0 && 'lg:border-l',
              index >= 2 && 'lg:border-t-0'
            )}
          >
            <a href={cell.href} className="block h-full px-5 py-4 no-underline hover:bg-slate-50">
              <span className="block text-base font-extrabold text-slate-900">{cell.term}</span>
              <span className="mt-0.5 block text-sm leading-snug text-slate-500">
                {cell.detail}
              </span>
            </a>
          </li>
        ))}
      </ul>

      <nav aria-label="On this page" className="mt-7 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold text-slate-500">On this page:</span>
        {JUMP_LINKS.map((link) => (
          <a
            key={link.href}
            href={link.href}
            className="rounded-full border border-solid border-slate-200 px-3.5 py-1.5 font-semibold text-slate-700 no-underline hover:border-primary hover:text-primary"
          >
            {link.label}
          </a>
        ))}
      </nav>

      <div className="prose mt-5 flex flex-col gap-6">
        <ContentCard id="how" className={ANCHOR}>
          <h2>How DiscountLock works</h2>
          <p className={TAGLINE}>
            Set it once at checkout. We handle the rest, and we tell you before we do anything.
          </p>

          <div className="not-prose my-5">
            <ol className="m-0 list-none space-y-4 p-0">
              {STEPS.map((step, index) => (
                <li key={step.lead} className="flex gap-4">
                  <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary text-sm font-extrabold text-white">
                    {index + 1}
                  </span>
                  <span className="text-slate-700">
                    <b className="font-bold text-slate-900">{step.lead}</b> {step.body}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          <p>
            Every renewal is a regular order in your account with its own order number and its own
            refund window, so you can review, cancel, or ask about it just like anything else you’ve
            bought from us.
          </p>

          <p className="text-sm text-slate-500">
            In some states, including Vermont, DiscountLock is enrolled only if you opt in at
            checkout. If it isn’t available where you live, it won’t be applied to your order and
            your subscription simply ends at the close of its term.
          </p>
        </ContentCard>

        <ContentCard id="savings" className={ANCHOR}>
          <h2>Guaranteed savings at every renewal</h2>
          <p className={TAGLINE}>You’ll never pay full price for a DiscountLock renewal.</p>

          <div className="not-prose my-4 grid gap-3 sm:grid-cols-3">
            {FACTS.map((fact) => (
              <div
                key={fact.term}
                className="rounded-lg border border-solid border-slate-200 bg-slate-50 px-3.5 py-3"
              >
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {fact.term}
                </div>
                <div className="mt-0.5 text-lg font-extrabold tracking-tight text-slate-900">
                  {fact.value}
                </div>
                <div className="mt-0.5 text-sm leading-snug text-slate-500">{fact.detail}</div>
              </div>
            ))}
          </div>

          <p>
            When it’s time to renew, we look for the best price we can offer. In most cases that’s
            the same price you paid. If the publisher has changed pricing, you still get at least
            10% off the list price at the time of renewal. You’ll see the exact amount in your 7-day
            email before anything is charged.
          </p>

          <p>
            If we can’t offer at least a 10% discount, we won’t renew automatically. We’ll email you
            instead, and you can decide whether to renew at the price we can offer.
          </p>

          <h3>If your magazine is discontinued</h3>

          <p>
            Sometimes a magazine stops publishing or is no longer available for renewal. When that
            happens we’ll offer you a similar magazine of equal or greater value, at the same or
            better price, and email you the details before your renewal date. Don’t want the
            substitute? Turn off DiscountLock for that subscription before the process date and
            nothing is charged.
          </p>
        </ContentCard>

        <ContentCard id="reminders" className={ANCHOR}>
          <h2>Two emails before every renewal</h2>
          <p className={TAGLINE}>You always know what’s coming, when, and for how much.</p>

          <div className="not-prose relative my-5">
            {/* The line the dots sit on, once they are in a row. */}
            <span
              aria-hidden
              className="absolute left-[16%] right-[16%] top-[17px] hidden h-0.5 bg-primary-200 md:block"
            />

            <ol className="m-0 grid list-none gap-5 p-0 md:grid-cols-3 md:gap-0">
              {TIMELINE.map((entry, index) => (
                <li
                  key={entry.term}
                  className="grid grid-cols-[2.25rem_1fr] gap-x-3.5 md:block md:px-3.5 md:text-center"
                >
                  <span
                    className={cn(
                      'relative z-10 row-span-2 flex h-9 w-9 items-center justify-center rounded-full text-xs font-extrabold text-white md:mx-auto md:mb-2.5',
                      // The last stop is the charge itself, not another warning.
                      index === TIMELINE.length - 1 ? 'bg-slate-900' : 'bg-primary'
                    )}
                  >
                    {entry.dot}
                  </span>
                  <span className="block font-extrabold text-slate-900">{entry.term}</span>
                  <span className="mt-1 block text-sm leading-snug text-slate-500">
                    {entry.detail}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          <h3>Your renewal date follows your order, not your mailing label</h3>

          <p>
            DiscountLock renews on the anniversary of the term you bought from us, counted from your
            order date. It doesn’t track the expiration date printed on your magazine label. If you
            already had a subscription when you ordered (for example, one you bought from the
            publisher), your renewal can process while that earlier term still has months, or even
            years, to run.
          </p>

          <p>
            <strong>Renewing early never costs you anything.</strong> The new term is added to the
            end of your current one, so nothing is shortened and you get no duplicates. You also
            lock in today’s discounted price before the publisher’s next increase, which is exactly
            why some customers renew early on purpose.
          </p>

          <p>
            <strong>Rather wait?</strong> Check the expiration date on your label, then open your{' '}
            <a href={accountUrl}>DiscountLock page</a> to delay the renewal or set the exact date
            you want it to process.
          </p>

          {/* The wrapper is what scrolls, not the table: `display:block` on the table
              itself is the usual shortcut and costs the element its table semantics in
              some screen readers. It takes a tabindex because a region that scrolls has
              to scroll without a mouse, and the table a floor, or the columns keep
              shrinking and there is nothing to scroll. */}
          <div className="not-prose my-4 w-full overflow-x-auto" tabIndex={0}>
            <table className="w-full min-w-[420px] border-collapse text-left text-sm text-slate-700">
              <thead>
                <tr>
                  <th className={HEADER_CELL}>Subscription type</th>
                  <th className={HEADER_CELL}>When the renewal processes</th>
                </tr>
              </thead>
              <tbody>
                {PROCESSING.map((row) => (
                  <tr key={row.type}>
                    <td className={CELL}>{row.type}</td>
                    <td className={CELL}>{row.when}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-sm text-slate-500">
            Terms are counted from your DiscountMags order date. The date on your magazine label may
            be later, and that’s fine: the renewal is added to the end of it.
          </p>
        </ContentCard>

        <ContentCard id="cancel" className={ANCHOR}>
          <span className="not-prose mb-2.5 inline-block rounded-full bg-primary-50 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-primary">
            You’re in control
          </span>

          {/* The pill is the card's first child, so this heading is not, and prose's
              heading margin would otherwise open a gap the pill was meant to fill. */}
          <h2 className="mt-0">Cancel anytime. Two easy ways.</h2>
          <p className={TAGLINE}>
            Turning off DiscountLock stops future renewals. Your current subscription continues
            through the end of its current term.
          </p>

          <div className="not-prose mt-4 grid gap-4 md:grid-cols-2">
            <div className="flex flex-col rounded-lg border border-solid border-primary-200 bg-primary-50 p-5">
              <div className="text-xs font-extrabold uppercase tracking-wider text-primary">
                Option 1 · About a minute
              </div>
              <h3 className="mb-2 mt-1 text-lg font-extrabold text-slate-900">
                Turn it off in your account
              </h3>
              <ul className="mb-4 ml-4 list-disc space-y-1.5 p-0 text-sm text-slate-700 marker:text-primary">
                <li>
                  Sign in and open <b className="font-bold text-slate-900">DiscountLock</b> in your
                  account menu.
                </li>
                <li>
                  Pick the subscription and choose{' '}
                  <b className="font-bold text-slate-900">Disable DiscountLock</b>.
                </li>
                <li>You’ll get a confirmation email right away.</li>
              </ul>
              <a href={accountUrl} className={`${BUTTON_FILLED} mt-auto self-start`}>
                Manage DiscountLock
              </a>
            </div>

            <div className="flex flex-col rounded-lg border border-solid border-slate-200 bg-white p-5">
              <div className="text-xs font-extrabold uppercase tracking-wider text-primary">
                Option 2
              </div>
              <h3 className="mb-2 mt-1 text-lg font-extrabold text-slate-900">Ask us to do it</h3>
              <ul className="mb-4 ml-4 list-disc space-y-1.5 p-0 text-sm text-slate-700 marker:text-primary">
                <li>
                  Can’t sign in, or would rather not? Send us a message anytime and we’ll turn it
                  off for you.
                </li>
                <li>
                  Include the name and mailing address on the subscription, or your order number.
                </li>
                <li>
                  Phone support is available to existing customers; the number is in your order
                  confirmation email and on your card statement.
                </li>
              </ul>
              <a href={contactUrl} className={`${BUTTON_OUTLINE} mt-auto self-start`}>
                Contact Customer Care
              </a>
              <div className="mt-2 text-xs text-slate-500">
                Automated order support 24/7 · Live agents {liveAgentHours()}
              </div>
            </div>
          </div>

          <h3>Good to know before you turn it off</h3>

          <ul>
            <li>
              <strong>It can’t be turned back on.</strong> Once DiscountLock is off for a
              subscription, the locked price is gone. To renew later, place a new order at the price
              available then.
            </li>
            <li>
              <strong>It’s per subscription.</strong> Turning it off for one magazine doesn’t affect
              your others.
            </li>
            <li>
              <strong>Renewal already processed?</strong> Cancel it within 6 months of the charge
              for a pro-rated refund of any unsent issues. Cancel before the process date and the
              renewal is refunded in full. Details are in our <a href={refundUrl}>refund policy</a>.
            </li>
            <li>
              <strong>Cancelling with the publisher doesn’t stop it.</strong> DiscountLock is
              managed here, in your DiscountMags account, not on the publisher’s site.
            </li>
          </ul>
        </ContentCard>

        <ContentCard id="compare" className={ANCHOR}>
          <h2>How DiscountLock compares</h2>
          <p className={TAGLINE}>
            Most sellers auto-renew. Few promise a discount or tell you the price first.
          </p>

          <div className="not-prose my-4 w-full overflow-x-auto" tabIndex={0}>
            <table className="w-full min-w-[560px] border-collapse text-left text-sm text-slate-700">
              <thead>
                <tr>
                  <th className={HEADER_CELL} />
                  <th className={US_HEADER_CELL}>DiscountMags</th>
                  <th className={HEADER_CELL}>Magazines.com</th>
                  <th className={HEADER_CELL}>Publisher direct</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row" className={HEADER_CELL}>
                    Renewal price
                  </th>
                  <td className={US_CELL}>At least 10% off list, guaranteed</td>
                  <td className={CELL}>May renew at a higher price with no minimum discount</td>
                  <td className={CELL}>May renew at a higher price with no minimum discount</td>
                </tr>
                <tr>
                  <th scope="row" className={HEADER_CELL}>
                    Notice before charge
                  </th>
                  <td className={US_CELL}>Two emails, 30 and 7 days ahead, with the exact price</td>
                  <td className={CELL}>Varies</td>
                  <td className={CELL}>Varies</td>
                </tr>
                <tr>
                  <th scope="row" className={HEADER_CELL}>
                    Cancellation
                  </th>
                  <td className={US_CELL}>Online in your account anytime, or contact us</td>
                  <td className={CELL}>Online</td>
                  <td className={CELL}>Varies</td>
                </tr>
              </tbody>
            </table>
          </div>
        </ContentCard>

        <div className="flex items-start gap-3.5 rounded-lg border border-solid border-primary-200 bg-primary-50 px-5 py-4">
          <span
            aria-hidden
            className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-primary text-white"
          >
            <FontAwesomeIcon icon={faArrowRotateLeft as IconProp} />
          </span>
          <div>
            <strong>Need a refund on a renewal that already went through?</strong>
            <p className="mb-0 mt-1 text-sm">
              Every DiscountLock renewal is covered by our <a href={refundUrl}>refund policy</a>:
              full refund if you cancel before the process date, pro-rated refund of unsent issues
              within 6 months after it. Start from <a href={ordersUrl}>My Orders</a>.
            </p>
          </div>
        </div>

        <section id="faqs" className={ANCHOR}>
          <h2 className="mt-0">Frequently asked questions</h2>

          <div className="mt-4 grid gap-2.5">
            {faqs.map((faq, index) => (
              <FaqAccordion
                key={faq.question}
                question={faq.question}
                answer={faq.answer}
                defaultOpen={index === 0}
              />
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

DiscountLockBenefits.layoutName = 'Layouts.Institutional';
